import { FC, useEffect, useState, MouseEvent } from "react";
import { styled } from "@mui/material/styles";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { getAllRoles, getAllUsers } from "../../../../redux/actions/authAction";
import { AddUserRoleDrawer } from "../../../../components/admin/add-user-role-drawer/add-user-role";
import { CustomButton } from "../../../../components";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export const RoleManagement: FC = () => {
  const dispatch = useAppDispatch();
  const {
    allUsers,
    allUsersLoading,
    updatePermissionsData,
    setRoleData,
    roles,
    rolesLoading,
  } = useAppSelector((state) => state.auth);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    dispatch(getAllRoles());
  }, []);

  useEffect(() => {
    dispatch(getAllRoles());
  }, [updatePermissionsData, setRoleData]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>, user: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setSelectedUser(null);
  };

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "3rem",
          marginTop: "4.357rem",
          paddingInline: "5rem",
          width: "100%",
        }}
      >
        <AddUserRoleDrawer
          open={openDrawer}
          toggleDrawer={handleDrawerClose}
          populateFor={selectedUser}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
            Role Management
          </Typography>
          <CustomButton
            sx={{ width: "11rem", height: "3rem" }}
            onClick={() => {
              setOpenDrawer(true);
              setSelectedUser(null);
            }}
          >
            Add New
          </CustomButton>
        </Box>

        <Box
          sx={{
            maxHeight: "51rem",
            width: "100%",
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "0.5rem" },
            "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
            "&::-webkit-scrollbar-thumb": { background: "#888" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
          }}
        >
          <Table sx={{ width: "100%" }} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Role Name</StyledTableCell>
                <StyledTableCell>Permissions</StyledTableCell>
                <StyledTableCell>Permission Count</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allUsersLoading && (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} sx={{ textAlign: "center" }}>
                    <CircularProgress size={24} color="inherit" />
                  </StyledTableCell>
                </StyledTableRow>
              )}

              {!allUsersLoading &&
                roles?.map((user) => {
                  const permissions = user?.permissions ?? [];
                  const displayedPermissions = permissions
                    .slice(0, 3)
                    .map((p) => p.description)
                    .join(", ");
                  const extra = permissions.length > 3 ? ", ..." : "";

                  return (
                    <StyledTableRow key={user.id}>
                      <StyledTableCell>{user?.name ?? "-"}</StyledTableCell>
                      <StyledTableCell>
                        {displayedPermissions}
                        {extra}
                      </StyledTableCell>
                      <StyledTableCell>{permissions.length}</StyledTableCell>
                      <StyledTableCell>
                        <IconButton onClick={(e) => handleClick(e, user)}>
                          <MoreHorizIcon
                            sx={{ fontSize: "2rem" }}
                            color="primary"
                          />
                        </IconButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}

              {!rolesLoading && roles?.length === 0 && (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} sx={{ textAlign: "center" }}>
                    No users found.
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          MenuListProps={{ "aria-labelledby": "basic-button" }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              setOpenDrawer(true);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Role</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </AdminLayout>
  );
};
